import { Node } from 'reactflow';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { addIdsToEdges, StoredGraphState } from './GraphContext';
import {
  deNormalizeGraph,
  mergeGraphs,
  NormalizedGraph,
  normalizeGraph,
} from 'packages/tapeBuilder/ui/normalize';
import { GraphPositions } from '~/packages/common-frontend/components/ReactFlow';

export const exportGraph = ({
  organizationSlug,
  artifactDocumentId,
  storedGraph,
  flowNodes,
}: {
  organizationSlug?: string;
  artifactDocumentId?: string;
  storedGraph: StoredGraphState;
  flowNodes: Node[];
}) => {
  const positions = flowNodes.reduce<GraphPositions>((acc, node) => {
    return {
      ...acc,
      [node.id]: node.position,
    };
  }, {});
  const { nodes, edges } = storedGraph;
  const { normalized } = normalizeGraph({ nodes, edges, positions });

  const blob = new Blob([JSON.stringify(normalized)], {
    type: 'application/json',
  });

  const env = commonUtils.EnvUtils.getEnv();

  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = `${
    organizationSlug ? `${organizationSlug}-` : ''
  }tape-builder-graph-${env}${
    artifactDocumentId ? `-${artifactDocumentId}` : ''
  }.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

type StoredGraphStateAndPositions = {
  storedGraph: StoredGraphState;
  positions: GraphPositions;
};

// return NormalizedGraph read from file.
const importInternal = (file: File): Promise<NormalizedGraph> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event && event.target) {
        const normalized = JSON.parse(event.target.result as string);

        // some basic checks to ensure we read the write file.
        if (normalized.nodes && normalized.edges && normalized.positions) {
          resolve(normalized);
        }
        reject(new Error('Invalid file missing nodes, edges or positions'));
      }
      reject(new Error('Could not read file'));
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsText(file);
  });
};

export const importGraph = async (
  file: File,
): Promise<StoredGraphStateAndPositions> => {
  const normalized = await importInternal(file);
  finleyAssert(normalized, 'Could not read file');
  const { nodes, edges, positions } = deNormalizeGraph({
    normalized,
    nodeIdToSlug: null, // we want node ids rotatated.
  });

  const edgesWithIds = addIdsToEdges(edges);
  return { storedGraph: { nodes, edges: edgesWithIds }, positions };
};

export const importAndMergeGraph = async (
  file: File,
  currentGraph: StoredGraphState,
): Promise<StoredGraphStateAndPositions> => {
  // 1st normalize current graph.
  const { normalized: normalizedCurrent, nodeIdToSlug } = normalizeGraph({
    nodes: currentGraph.nodes,
    edges: currentGraph.edges,
    positions: {},
  });
  const normalizedImported = await importInternal(file);

  const mergedNormalized = mergeGraphs([normalizedImported, normalizedCurrent]);
  const { nodes, edges, positions } = deNormalizeGraph({
    normalized: mergedNormalized,
    nodeIdToSlug, // we want node ids from current graph to be used.
  });

  const edgesWithIds = addIdsToEdges(edges);
  return { storedGraph: { nodes, edges: edgesWithIds }, positions };
};
